// src/utils/searchSimilarity.ts

/**
 * Similarity between two phrases.
 * Returns a ratio 0..1.
 * Two phrases are "X% similar" when every word in one is found in the other.
 * We use a Jaccard-style score on word sets, with full containment boosted to 1.0.
 */
const tokenize = (term: string): string[] =>
  term
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);

export const similarity = (a: string, b: string): number => {
  const A = tokenize(a);
  const B = tokenize(b);
  if (A.length === 0 || B.length === 0) return 0;
  if (a.toLowerCase().trim() === b.toLowerCase().trim()) return 1;

  const setA = new Set(A);
  const setB = new Set(B);

  let inter = 0;
  setA.forEach((w) => {
    if (setB.has(w)) inter++;
  });

  if (inter === 0) return 0;

  const onlyA = setA.size - inter;
  const onlyB = setB.size - inter;

  // Full containment -> 1.0
  if (onlyA === 0 || onlyB === 0) return 1;

  // Otherwise use combined token coverage (intersection / union)
  const union = setA.size + setB.size - inter;
  return inter / union;
};

export interface SearchGroup {
  leading: string;
  totalSearchCount: number;
  totalStars: number;
  members: { term: string; count: number; similarityToLeading: number }[];
}

/**
 * Group search terms based on a similarity threshold.
 * Returns groups where each group is anchored on the most frequent term.
 */
export const groupSimilarTerms = (
  terms: { term: string; count: number }[],
  threshold: number
): SearchGroup[] => {
  const sorted = [...terms].sort((a, b) => b.count - a.count);
  const used = new Set<string>();
  const groups: SearchGroup[] = [];

  for (const seed of sorted) {
    if (used.has(seed.term)) continue;
    used.add(seed.term);

    const members: SearchGroup['members'] = [
      { term: seed.term, count: seed.count, similarityToLeading: 1 },
    ];

    for (const candidate of sorted) {
      if (used.has(candidate.term)) continue;
      const sim = similarity(seed.term, candidate.term);
      if (sim >= threshold) {
        used.add(candidate.term);
        members.push({
          term: candidate.term,
          count: candidate.count,
          similarityToLeading: sim,
        });
      }
    }

    // Sort members: leading first, then by count desc
    members.sort((a, b) => {
      if (a.term === seed.term) return -1;
      if (b.term === seed.term) return 1;
      return b.count - a.count;
    });

    const totalSearchCount = members.reduce((s, m) => s + m.count, 0);

    groups.push({
      leading: seed.term,
      totalSearchCount,
      totalStars: 0,
      members,
    });
  }

  // Sort groups by total search count desc
  groups.sort((a, b) => b.totalSearchCount - a.totalSearchCount);
  return groups;
};

export type TimeFilterKey =
  | 'newest'
  | 'last24h'
  | 'last7d'
  | 'last30d'
  | 'last90d'
  | 'lastYear'
  | 'last2y'
  | 'last5y'
  | 'last12y'
  | 'all';

export const timeFilterLabels: Record<TimeFilterKey, string> = {
  newest: 'Newest First',
  last24h: 'Last 24 Hours',
  last7d: 'Last 7 Days',
  last30d: 'Last 30 Days',
  last90d: 'Last 90 Days',
  lastYear: 'Last Year',
  last2y: 'Last 2 Years',
  last5y: 'Last 5 Years',
  last12y: 'Last 12 Years',
  all: 'All Time',
};

export const getCutoffMs = (key: TimeFilterKey): number => {
  const now = Date.now();
  const day = 24 * 60 * 60 * 1000;
  switch (key) {
    case 'newest':
      return 0;
    case 'last24h':
      return now - day;
    case 'last7d':
      return now - 7 * day;
    case 'last30d':
      return now - 30 * day;
    case 'last90d':
      return now - 90 * day;
    case 'lastYear':
      return now - 365 * day;
    case 'last2y':
      return now - 2 * 365 * day;
    case 'last5y':
      return now - 5 * 365 * day;
    case 'last12y':
      return now - 12 * 365 * day;
    case 'all':
    default:
      return 0;
  }
};